import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Link, Chip, Divider } from "@mui/material";
import { ROUTES } from "app/utils/constants/routeConstants";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import useLoginRedirect from "app/hooks/useLoginRedirect";
import jwtAuthAxios from "app/services/auth/jwtAuth";
import { GET_TAGS } from "app/utils/constants/paths";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";
import { getSocialIcon } from "components/SocialMediaIcons";

const links = [
  {
    link: "https://www.youtube.com/@Codingpal",
    platform: "YOUTUBE",
    name: "@CodingPal",
  },
  { link: "https://x.com/RealCodingPal", platform: "X", name: "RealCodingPal" },
  {
    link: "https://www.linkedin.com/company/codingpal",
    platform: "LINKEDIN",
    name: "codingPal",
  },
  {
    link: "https://www.instagram.com/codingpal/",
    platform: "INSTAGRAM",
    name: "codingPal",
  },
];

const Footer = () => {
  const { authUser } = useJumboAuth();
  const isLoggedIn = !!authUser;
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [errorTags, setErrorTags] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { LoginDialog } = useLoginRedirect({
    isLoggedIn,
    dialogMessage:
      "You need to be logged in to report an issue. Please log in to continue",
    confirmColor: "success",
  });

  const handleReportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoggedIn) {
      navigate(ROUTES.REPORT || "/report");
    } else {
      setDialogOpen(true);
    }
  };

  const handleLearningLinkClick = (e, to) => {
    e.preventDefault();
    navigate(to);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoadingTags(true);
        const response = await jwtAuthAxios.get(`${GET_TAGS}?size=50&page=0`);
        setTags(response.data);
      } catch (err) {
        setErrorTags(err.message);
      } finally {
        setLoadingTags(false);
      }
    };
    fetchTags();
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        py: 4,
        px: { xs: 2, sm: 4, md: 6 },
        borderTop: 2,
        borderColor: "divider",
      }}
    >
      <Box textAlign="center">
        <Typography variant="h6" fontWeight={600}>
          Made in 🇮🇳 with ❤️ for the 🌏
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          CodingPal.org © {new Date().getFullYear()}
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          CODINGPAL LEARNING SOLUTIONS LLP
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={4}>
        {/* Follow Us */}
        <Grid item xs={6} sm={6} md={3}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Follow Us
          </Typography>
          {links.map(({ link, name, platform }) => {
            const icon = getSocialIcon(platform);
            return icon ? (
              <Box key={platform} display="flex" alignItems="center" mb={1}>
                <Box mr={1}>{icon}</Box>
                <Link
                  href={link}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  color="text.primary"
                >
                  {name}
                </Link>
              </Box>
            ) : null;
          })}
        </Grid>

        {/* Company */}
        <Grid item xs={6} sm={6} md={3}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Company
          </Typography>
          {[
            { to: ROUTES.ABOUT_US, label: "About Us" },
            { to: ROUTES.CONTACT_US, label: "Contact Us" },
            { to: ROUTES.TERMS_AND_CONDITIONS, label: "Terms & Conditions" },
            {
              to: ROUTES.CANCELLATION_REFUND_POLICY,
              label: "Cancellation & Refund Policy",
            },
            { to: ROUTES.PRIVACY_POLICY, label: "Privacy Policy" },
          ].map(({ to, label }) => (
            <Link
              key={label}
              component={RouterLink}
              to={to}
              onClick={handleLinkClick}
              underline="hover"
              display="block"
              mb={1}
              color="text.primary"
            >
              {label}
            </Link>
          ))}
        </Grid>

        {/* CodingPal */}
        <Grid item xs={6} sm={6} md={3}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            CodingPal
          </Typography>
          {[
            { to: ROUTES.USER_SIGNUP, label: "Signup" },
            { to: ROUTES.USER_LOGIN, label: "Signin" },
            { to: ROUTES.ALGOS_AND_DS, label: "Data Structures & Algorithms" },
            { to: ROUTES.UPGRADE, label: "Premium Plans" },
            { label: "Have something? Tell us", onClick: handleReportClick },
          ].map(({ to, label, onClick }) =>
            to ? (
              <Link
                key={label}
                component={RouterLink}
                to={to}
                onClick={handleLinkClick}
                underline="hover"
                display="block"
                mb={1}
                color="text.primary"
              >
                {label}
              </Link>
            ) : (
              <Typography
                key={label}
                component="button"
                onClick={onClick}
                underline="hover"
                display="block"
                mb={1}
                color="text.primary"
                aria-label="Report an issue, requires login"
                sx={{
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  font: "inherit",
                  px: 0,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {label}
              </Typography>
            )
          )}
        </Grid>

        {/* CodingPal Learning */}
        <Grid item xs={6} sm={6} md={3}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            CodingPal Learning
          </Typography>
          {[
            {
              to: "/algos-and-ds?size=25&page=1&sort=id%2Casc&ForPaidOnly=false",
              label: "Try free problems",
            },
            {
              to: "/algos-and-ds?size=25&page=1&sort=id%2Casc&ProblemVideos=true",
              label: "Problems with Videos",
            },
          ].map(({ to, label }) => (
            <Typography
              key={label}
              component="button"
              onClick={(e) => handleLearningLinkClick(e, to)}
              underline="hover"
              display="block"
              mb={1}
              color="text.primary"
              tabIndex={0}
              sx={{
                cursor: "pointer",
                border: "none",
                background: "none",
                px: 0,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {label}
            </Typography>
          ))}
        </Grid>
      </Grid>

      {/* Explore Topics */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Explore Topics
        </Typography>
        {loadingTags ? (
          typeof LoadingView === "function" ? (
            <LoadingView message="Loading topics..." />
          ) : (
            <Typography variant="body2">Loading topics...</Typography>
          )
        ) : errorTags ? (
          typeof ErrorView === "function" ? (
            <ErrorView error={errorTags} />
          ) : (
            <Typography variant="body2" color="error">
              Error: {errorTags}
            </Typography>
          )
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {tags
              .filter((tag) => tag.active)
              .slice(0, 50)
              .map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  component={RouterLink}
                  to={`${ROUTES.ALGOS_AND_DS}?Tags=${encodeURIComponent(
                    tag.name
                  )}`}
                  onClick={handleLinkClick}
                  clickable
                  sx={{
                    bgcolor: "grey.200",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                    },
                  }}
                />
              ))}
          </Box>
        )}
      </Box>

      {/* <Divider sx={{ my: 3 }} />

      <Box textAlign="center">
        <Typography variant="h6" fontWeight={600}>
          CodingPal Learning Solutions LLP
        </Typography>
      </Box> */}
      {/* Login Dialog */}
      {LoginDialog && React.isValidElement(LoginDialog)
        ? React.cloneElement(LoginDialog, {
            open: dialogOpen,
            onClose: handleDialogClose,
            message:
              "You need to be logged in to report an issue. Please log in to continue",
            confirmColor: "success",
          })
        : dialogOpen && (
            <Typography color="error">Dialog failed to load</Typography>
          )}
    </Box>
  );
};

export default Footer;
